import React, { useState } from "react";
import { Share2 } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { tryNativeShare } from "@/lib/share";
import SharePopupMenu from "@/components/SharePopupMenu";

function dateLabel(iso, lang) {
    if (!iso) return "";
    const d = new Date(`${iso}T00:00:00`);
    return d.toLocaleDateString(lang === "it" ? "it-IT" : "en-GB", {
        weekday: "short", day: "2-digit", month: "short",
    });
}

/**
 * Share a Ride Call entry via WhatsApp / IG / FB / Telegram. Builds a compact
 * message with date, route, level/style, meeting point, spots left, and a
 * deep link back to the rides page.
 */
export default function ShareRideButton({ ride, trailName, isFull, spotsLeft, className = "" }) {
    const { t, lang } = useLang();
    const [open, setOpen] = useState(false);

    const lines = [];
    lines.push(`🚵 ${t("rideCall.shareEyebrow").toUpperCase()}`);
    lines.push(`📅 ${dateLabel(ride.date, lang)} · ${ride.time}`);
    lines.push(`📍 ${trailName}`);
    const meta = [
        `${t("rideCall.shareLevel")}: ${t(`rideCall.levels.${ride.level}`)}`,
        `${t("rideCall.shareStyle")}: ${t(`rideCall.styles.${ride.style}`)}`,
    ];
    lines.push(`🎯 ${meta.join(" · ")}`);
    lines.push(`📌 ${t("rideCall.shareMeet")}: ${ride.meeting_point}`);
    if (isFull) {
        lines.push(`👥 ${t("rideCall.full")}`);
    } else {
        lines.push(`👥 ${spotsLeft}/${ride.spots_total} ${t("rideCall.shareSpots")}`);
    }
    if (ride.notes) lines.push(`\n"${ride.notes}"`);
    lines.push(`\n${t("rideCall.shareCta")} →`);

    const text = lines.join("\n");
    const url = `${window.location.origin}/ride-call`;
    const title = t("rideCall.shareTitle");

    const onClick = async () => {
        const result = await tryNativeShare({ title, text, url });
        if (!result.used) setOpen((o) => !o);
    };

    return (
        <div className={`relative inline-block ${className}`}>
            <button
                type="button"
                onClick={onClick}
                data-testid={`ride-share-${ride.id}`}
                className="text-xs uppercase tracking-[0.2em] font-bold border border-abnormal-acid text-abnormal-acid px-4 py-2 hover:bg-abnormal-acid hover:text-black transition-colors inline-flex items-center gap-2"
            >
                <Share2 size={12} />
                {t("rideCall.share")}
            </button>
            {open && (
                <SharePopupMenu
                    text={text}
                    url={url}
                    title={title}
                    onClose={() => setOpen(false)}
                    testidPrefix={`ride-share-${ride.id}`}
                />
            )}
        </div>
    );
}
